import React from 'react';
import { Link } from 'react-router-dom';
import cta1Image from '../cta1.webp';
import cta2Image from '../cta2.webp';

/**
 * Banner CTA reutilizável (Home e Sobre Nós).
 * @param {object} props
 * @param {boolean} [props.enabled]
 * @param {string} [props.ctaLayout]
 * @param {string} [props.ctaTitle]
 * @param {string} [props.ctaDescription]
 * @param {string} [props.ctaButtonText]
 * @param {string} [props.ctaImageSource]
 * @param {string} [props.ctaSystemImage]
 * @param {string} [props.ctaImageStyle]
 * @param {string|null} [props.ctaImage]
 * @param {string} [props.primaryColor]
 */
function CtaBanner({
  enabled,
  ctaLayout,
  ctaTitle,
  ctaDescription,
  ctaButtonText,
  ctaImageSource,
  ctaSystemImage,
  ctaImageStyle,
  ctaImage,
  primaryColor,
}) {
  if (!enabled) return null;

  const color = primaryColor || '#3B82F6';
  const resolvedCtaImage =
    ctaImageSource === 'upload' && ctaImage
      ? ctaImage
      : (ctaSystemImage || 'cta1') === 'cta2'
        ? cta2Image
        : cta1Image;
  const resolvedImageStyle =
    ctaImageSource === 'upload'
      ? ctaImageStyle || 'noBackground'
      : (ctaSystemImage || 'cta1') === 'cta2'
        ? 'withBackground'
        : ctaImageStyle || 'noBackground';
  const applyBloom = (e, bloomColor) => {
    e.currentTarget.style.boxShadow = `0 0 0 2px ${bloomColor}40, 0 0 12px 2px ${bloomColor}45`;
  };
  const clearBloom = (e) => {
    e.currentTarget.style.boxShadow = '';
  };
  const ctaLabel = ctaButtonText || 'Entrar em contato';
  const title = ctaTitle || 'Pronto para encontrar seu próximo imóvel?';

  if ((ctaLayout || 'withImage') === 'noImage') {
    return (
      <section className="py-12 md:py-16" data-aos="fade-up">
        <div className="max-w-7xl mx-auto px-4">
          <div
            className="relative overflow-hidden rounded-2xl px-6 py-12 md:px-12 md:py-14 text-center"
            style={{
              background: `linear-gradient(120deg, ${color} 0%, ${color}cc 55%, ${color}99 100%)`,
            }}
          >
            <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden>
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/40" />
              <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-white/25" />
            </div>
            <div className="relative max-w-2xl mx-auto space-y-4">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
                {title}
              </h2>
              {ctaDescription && (
                <p className="text-base md:text-lg text-white/90 leading-relaxed">
                  {ctaDescription}
                </p>
              )}
              <div className="pt-2">
                <Link
                  to="/contato#contact-form"
                  className="inline-flex items-center justify-center px-8 py-3.5 rounded-full font-semibold bg-white shadow-md transition-[box-shadow] duration-300"
                  style={{ color }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      '0 0 0 2px rgba(255,255,255,0.4), 0 0 12px 3px rgba(255,255,255,0.28)';
                  }}
                  onMouseLeave={clearBloom}
                >
                  {ctaLabel}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (resolvedImageStyle !== 'withBackground') {
    return (
      <section className="py-12 md:py-16" data-aos="fade-up">
        <div className="max-w-7xl mx-auto px-4">
          <div
            className="relative overflow-hidden rounded-[1.75rem] border border-slate-200/80"
            style={{
              background: `linear-gradient(135deg, ${color}10 0%, #eef3f8 45%, ${color}18 100%)`,
            }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-6 items-center px-8 py-10 md:px-12 md:py-12">
              <div className="text-center lg:text-left space-y-4 order-2 lg:order-1">
                <h2 className="text-2xl md:text-3xl lg:text-[2.15rem] font-bold text-slate-900 leading-tight">
                  {title}
                </h2>
                {ctaDescription && (
                  <p className="text-sm md:text-base text-slate-500 leading-relaxed max-w-md mx-auto lg:mx-0">
                    {ctaDescription}
                  </p>
                )}
                <div className="pt-1">
                  <Link
                    to="/contato#contact-form"
                    className="inline-flex items-center justify-center px-7 py-3 rounded-xl font-semibold text-white uppercase tracking-wide text-sm shadow-md transition-[box-shadow] duration-300"
                    style={{ backgroundColor: color }}
                    onMouseEnter={(e) => applyBloom(e, color)}
                    onMouseLeave={clearBloom}
                  >
                    {ctaLabel}
                  </Link>
                </div>
              </div>
              <div className="flex justify-center lg:justify-end order-1 lg:order-2">
                <img
                  src={resolvedCtaImage}
                  alt=""
                  className="w-full max-w-md lg:max-w-lg h-auto object-contain drop-shadow-sm"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-16" data-aos="fade-up">
      <div className="max-w-7xl mx-auto px-4">
        <div
          className="relative overflow-hidden rounded-[1.75rem] min-h-[280px] md:min-h-[340px]"
          style={{ backgroundColor: color }}
        >
          <div className="relative z-10 flex flex-col justify-center w-full lg:max-w-[48%] xl:max-w-[46%] px-8 py-10 md:px-12 md:py-14 text-center lg:text-left space-y-4">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
              {title}
            </h2>
            {ctaDescription && (
              <p className="text-sm md:text-base text-white/80 leading-relaxed max-w-md mx-auto lg:mx-0">
                {ctaDescription}
              </p>
            )}
            <div className="pt-2">
              <Link
                to="/contato#contact-form"
                className="inline-flex items-center justify-center px-8 py-3 rounded-full font-semibold text-sm uppercase tracking-wide bg-white shadow-md transition-[box-shadow] duration-300"
                style={{ color }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    '0 0 0 2px rgba(255,255,255,0.35), 0 0 14px 3px rgba(255,255,255,0.28)';
                }}
                onMouseLeave={clearBloom}
              >
                {ctaLabel}
              </Link>
            </div>
          </div>

          <div className="relative mx-auto mt-2 mb-8 h-[240px] w-[240px] sm:h-[280px] sm:w-[280px] lg:absolute lg:m-0 lg:top-1/2 lg:right-[-6%] lg:-translate-y-1/2 lg:h-[160%] lg:w-auto lg:aspect-square">
            <div className="h-full w-full rounded-full overflow-hidden">
              <img
                src={resolvedCtaImage}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CtaBanner;
